import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { Card, CardBody, Typography } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheckCircle, faHistory, faPencilAlt, faSyncAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import usePermissions from '../../common/usePermissions';
import ConfirmModal from '../../common/ConfirmModal';
import { rootUrl } from '../../../config/config';
import { formatMoney } from './cashbookStatementUtils';

const ENTRY_TYPE_STYLES = {
    INCOME:       { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Income' },
    EXPENSE:      { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Expense' },
    TRANSFER_IN:  { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Transfer In' },
    TRANSFER_OUT: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Transfer Out' },
};

const DELETE_MESSAGES = {
    EXPENSE:          'This will permanently delete this cashbook entry and the linked Expense record. Child records (attachments, approvals) are also removed. This cannot be undone.',
    VENDOR_PAYMENT:   'This will permanently delete this cashbook entry and the linked Vendor Ledger entry. This cannot be undone.',
    OWNER_TRANS:      'This will permanently delete this cashbook entry and the linked Owner Transaction. This cannot be undone.',
    PARTS_TRANS:      'This will permanently delete this cashbook entry, the linked Parts Transaction, and any associated Vendor Ledger entries. This cannot be undone.',
    BANK_TRANSFER:    'This will permanently delete both legs of this bank transfer (the debit and the credit entry). This cannot be undone.',
    JOB_CARD:         'This will permanently delete this cashbook entry. The linked Job Card will be kept but its cashbook reference will be cleared.',
    PART_SALE:        'This will permanently delete this cashbook entry. The linked Job Card Parts record will be kept but its cashbook reference will be cleared.',
    REMITTANCE_BATCH: 'This will permanently delete this cashbook entry. The linked batch record will be kept but its cashbook reference will be cleared.',
    WEEKLY_REMITTANCE:'This will permanently delete this cashbook entry. The linked batch record will be kept but its cashbook reference will be cleared.',
    PAYMENT_BATCH:    'This will permanently delete this cashbook entry. The linked batch record will be kept but its cashbook reference will be cleared.',
    REVERSAL:         'This will permanently delete this reversal entry. The original transaction it reversed will remain in the ledger and will appear un-reversed. This cannot be undone.',
    OWNER_TRANS_REV:  'This will permanently delete this reversal entry. The original transaction it reversed will remain in the ledger and will appear un-reversed. This cannot be undone.',
    MANUAL:           'This will permanently delete this cashbook entry. This cannot be undone.',
    OTHER:            'This will permanently delete this cashbook entry. This cannot be undone.',
};

const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'INCOME', label: 'Income' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'TRANSFER_IN', label: 'Transfer In' },
    { value: 'TRANSFER_OUT', label: 'Transfer Out' },
];

const reconciledOptions = [
    { value: '', label: 'All Status' },
    { value: 'true', label: 'Reconciled' },
    { value: 'false', label: 'Not Reconciled' },
];

const selectStyles = {
    control: (base) => ({
        ...base,
        borderRadius: '8px',
        borderColor: '#e5e7eb',
        fontSize: '13px',
        boxShadow: 'none',
        '&:hover': { borderColor: '#FF5733' },
    }),
};

const PAGE_SIZE = 200;

const CashbookTransactions = () => {
    const { accountId } = useParams();
    const { token, user } = useAuth();
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();

    const authHeader = { Authorization: `Bearer ${token || localStorage.getItem('authToken')}` };
    const canDelete = hasPermission('cashbook.entries.delete');

    const [account, setAccount] = useState(null);
    const [entries, setEntries] = useState([]);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [weeks, setWeeks] = useState([]);
    const [filters, setFilters] = useState(() => {
        const now = new Date();
        const jan1 = new Date(now.getFullYear(), 0, 1);
        const dow = jan1.getDay();
        let daysUntilMon = dow === 0 ? 1 : (8 - dow) % 7;
        if (dow === 1) daysUntilMon = 0;
        const firstMon = new Date(now.getFullYear(), 0, 1 + daysUntilMon);
        const weekNum = Math.floor((now - firstMon) / (7 * 86400000)) + 1;
        const currentWeek = `${now.getFullYear()}_${String(weekNum).padStart(2, '0')}`;
        return { entry_type: '', date_from: '', date_to: '', reconciled: '', week_key: currentWeek };
    });

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchAccount = useCallback(async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/cashbook/accounts/${accountId}`, { headers: authHeader });
            if (res.data?.success) setAccount(res.data.data);
        } catch {
            // non-fatal
        }
    }, [accountId, token]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchWeeks = useCallback(async () => {
        try {
            const res = await axios.get(`${rootUrl}/system/weeks`, { headers: authHeader });
            if (res.data?.success) setWeeks(res.data.data);
        } catch {
            // non-fatal
        }
    }, [token]);

    // pageOffset is passed explicitly to avoid stale-closure bugs with the offset state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchEntries = useCallback(async (reset = false, pageOffset = 0) => {
        if (!accountId) return;
        setLoading(true);
        try {
            const params = { account_id: accountId, limit: PAGE_SIZE, offset: pageOffset };
            if (filters.week_key) {
                params.week_key = filters.week_key;
            } else {
                if (filters.date_from) params.date_from = filters.date_from;
                if (filters.date_to) params.date_to = filters.date_to;
            }
            if (filters.entry_type) params.entry_type = filters.entry_type;
            if (filters.reconciled !== '') params.reconciled = filters.reconciled;

            const res = await axios.get(`${rootUrl}/api/cashbook/entries`, { headers: authHeader, params });
            const data = res.data?.success ? res.data.data : (Array.isArray(res.data) ? res.data : []);
            setEntries(prev => reset ? data : [...prev, ...data]);
        } catch {
            if (reset) setEntries([]);
        } finally {
            setLoading(false);
        }
    }, [accountId, token, filters]);

    useEffect(() => {
        fetchAccount();
        fetchWeeks();
    }, [fetchAccount, fetchWeeks]);

    useEffect(() => {
        setOffset(0);
        fetchEntries(true, 0);
    }, [fetchEntries]);

    const handleLoadMore = () => {
        const newOffset = offset + PAGE_SIZE;
        setOffset(newOffset);
        fetchEntries(false, newOffset);
    };

    const handleDeleteClick = (entry) => {
        setDeleteTarget(entry);
        setDeleteError('');
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            const res = await axios.delete(`${rootUrl}/api/cashbook/entries/${deleteTarget.id}`, { headers: authHeader });
            if (res.data?.success) {
                setDeleteTarget(null);
                setOffset(0);
                fetchEntries(true, 0);
            } else {
                setDeleteError(res.data?.message || 'Delete failed.');
            }
        } catch (err) {
            setDeleteError(err.response?.data?.message || 'Delete failed.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleEditClick = (entry) => {
        setEditTarget(entry);
        setEditError('');
        const ownerName = entry.owner_name || '';
        const payRef = entry.payment_reference || '';
        const rawDesc = entry.description || '';
        // Strip existing owner-name prefix so backend can re-apply it cleanly
        const strippedDesc = ownerName
            ? rawDesc.replace(new RegExp(`^${ownerName}\\s*-\\s*`, 'i'), '').trim()
            : rawDesc;
        setEditForm({
            description: strippedDesc || payRef,
            entry_date: entry.entry_date ? entry.entry_date.split('T')[0] : '',
            amount: entry.amount || '',
            payment_method: entry.payment_method || '',
            payment_reference: payRef,
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editTarget) return;
        setEditLoading(true);
        setEditError('');
        try {
            const payload = { ...editForm, edit_by: user?.username || 'system' };
            const res = await axios.patch(`${rootUrl}/api/cashbook/entries/${editTarget.id}`, payload, { headers: authHeader });
            if (res.data?.success) {
                setEditTarget(null);
                setOffset(0);
                fetchEntries(true, 0);
            } else {
                setEditError(res.data?.message || 'Update failed.');
            }
        } catch (err) {
            setEditError(err.response?.data?.message || 'Update failed.');
        } finally {
            setEditLoading(false);
        }
    };

    const formatDate = (d) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatTime = (d) => {
        if (!d) return '';
        return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const isCredit = (entry) => entry.entry_type === 'INCOME' || entry.entry_type === 'TRANSFER_IN';

    const hasMore = entries.length > 0 && entries.length % PAGE_SIZE === 0;

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-4 md:p-8">
            {/* Header */}
            <div className="mb-6 flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => navigate('/cashbook/accounts')}
                    className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#FF5733] transition-colors"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Accounts
                </button>
                <span className="text-slate-300">/</span>
                <Typography className="text-base font-bold text-[#2a2e30]">
                    {account ? account.account_name : `Account ${accountId}`}
                </Typography>
                <span className="text-slate-300">/</span>
                <Typography className="text-sm text-slate-500">Transactions</Typography>
            </div>

            {/* Filter bar */}
            <Card className="border border-[#eadfd8] shadow-none rounded-xl bg-white mb-4">
                <CardBody className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
                        <div>
                            <Typography className="text-base font-bold text-[#2a2e30]">Transactions</Typography>
                            <Typography className="text-sm text-slate-500">
                                {account?.account_name ?? `Account ${accountId}`}
                                {canDelete && <span className="ml-2 text-xs font-semibold text-red-500 uppercase tracking-wide">· Superuser Mode</span>}
                            </Typography>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFilters({ entry_type: '', date_from: '', date_to: '', reconciled: '', week_key: '' })}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none self-start lg:self-auto"
                        >
                            Reset
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[160px_200px_minmax(0,1fr)_180px] gap-4">
                        <div>
                            <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Type</Typography>
                            <Select
                                options={typeOptions}
                                value={typeOptions.find(o => o.value === filters.entry_type)}
                                onChange={(opt) => setFilters(f => ({ ...f, entry_type: opt.value }))}
                                styles={selectStyles}
                                isSearchable={false}
                            />
                        </div>
                        <div>
                            <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Week</Typography>
                            <Select
                                options={[{ value: '', label: 'All Weeks' }, ...weeks.map(w => ({ value: w.week, label: `${w.week}` }))]}
                                value={filters.week_key ? { value: filters.week_key, label: filters.week_key } : { value: '', label: 'All Weeks' }}
                                onChange={(opt) => setFilters(f => ({ ...f, week_key: opt?.value || '', date_from: '', date_to: '' }))}
                                styles={selectStyles}
                                isClearable
                                placeholder="Select week..."
                            />
                        </div>
                        <div>
                            <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Date Range</Typography>
                            <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                                <input
                                    type="date"
                                    disabled={!!filters.week_key}
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:ring-1 focus:ring-[#FF5733] outline-none ${filters.week_key ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                                    value={filters.date_from}
                                    onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                                />
                                <span className="text-slate-400 text-sm text-center">to</span>
                                <input
                                    type="date"
                                    disabled={!!filters.week_key}
                                    className={`w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:ring-1 focus:ring-[#FF5733] outline-none ${filters.week_key ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                                    value={filters.date_to}
                                    onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div>
                            <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Reconciliation</Typography>
                            <Select
                                options={reconciledOptions}
                                value={reconciledOptions.find(o => o.value === filters.reconciled)}
                                onChange={(opt) => setFilters(f => ({ ...f, reconciled: opt.value }))}
                                styles={selectStyles}
                                isSearchable={false}
                            />
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Table */}
            {loading && entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-[#eadfd8]">
                    <FontAwesomeIcon icon={faSyncAlt} spin className="text-3xl text-[#FF5733] mb-4" />
                    <span className="text-sm font-medium text-gray-500">Loading transactions...</span>
                </div>
            ) : entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-[#eadfd8] border-dashed">
                    <div className="p-4 bg-gray-50 rounded-full mb-4">
                        <FontAwesomeIcon icon={faHistory} className="text-3xl text-gray-300" />
                    </div>
                    <Typography variant="h6" className="text-gray-400 font-bold">No transactions found</Typography>
                    <p className="text-sm text-gray-400">Try adjusting your filters</p>
                </div>
            ) : (
                <>
                    <div className="bg-white rounded-xl border border-[#eadfd8] overflow-hidden shadow-none">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[900px] text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50">
                                        <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Date</th>
                                        <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Type</th>
                                        <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Description</th>
                                        <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Ref Type</th>
                                        <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-right">Amount</th>
                                        <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-right">Balance After</th>
                                        <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-center">Status</th>
                                        <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-center">Edit</th>
                                        {canDelete && (
                                            <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-center">Del</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((entry) => {
                                        const style = ENTRY_TYPE_STYLES[entry.entry_type] ?? { bg: 'bg-gray-100', text: 'text-gray-700', label: entry.entry_type };
                                        const credit = isCredit(entry);
                                        return (
                                            <tr key={entry.id} className="hover:bg-[#fffaf7] transition-colors group">
                                                <td className="p-3 border-b border-slate-100">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold text-gray-700">{formatDate(entry.entry_date)}</span>
                                                        <span className="text-[10px] text-gray-400 font-medium">{formatTime(entry.entry_date)}</span>
                                                    </div>
                                                </td>
                                                <td className="p-3 border-b border-slate-100">
                                                    <span className={`px-2 py-1 rounded-sm text-[10px] font-bold tracking-tight ${style.bg} ${style.text}`}>
                                                        {style.label}
                                                    </span>
                                                </td>
                                                <td className="p-3 border-b border-slate-100">
                                                    <div className="max-w-xs xl:max-w-md">
                                                        <Typography className="text-sm font-medium text-gray-600 truncate">
                                                            {entry.description || '-'}
                                                        </Typography>
                                                        {entry.reference_no && (
                                                            <span className="text-[10px] text-gray-400 font-mono">Ref: {entry.reference_no}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-3 border-b border-slate-100">
                                                    <span className="text-[11px] text-slate-500 font-mono">
                                                        {entry.reference_type || '—'}
                                                    </span>
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-right">
                                                    <span className={`text-sm font-black ${credit ? 'text-green-600' : 'text-red-600'}`}>
                                                        {credit ? '+' : '-'}{formatMoney(entry.amount)}
                                                    </span>
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-right">
                                                    <span className="text-sm font-bold text-gray-900">{formatMoney(entry.balance_after)}</span>
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-center">
                                                    {entry.reconciled ? (
                                                        <div className="flex flex-col items-center">
                                                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-xs" />
                                                            <span className="text-[8px] font-black text-green-600 mt-0.5">VERIFIED</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center opacity-30 group-hover:opacity-100 transition-opacity">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                                                            <span className="text-[8px] font-bold text-gray-400 mt-0.5 uppercase">Pending</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditClick(entry)}
                                                        disabled={entry.reconciled}
                                                        className="text-slate-300 hover:text-orange-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                        title={entry.reconciled ? 'Cannot edit reconciled entry' : 'Edit entry'}
                                                    >
                                                        <FontAwesomeIcon icon={faPencilAlt} className="text-sm" />
                                                    </button>
                                                </td>
                                                {canDelete && (
                                                    <td className="p-3 border-b border-slate-100 text-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteClick(entry)}
                                                            disabled={entry.reconciled}
                                                            className="text-slate-300 hover:text-red-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                            title={entry.reconciled ? 'Cannot delete reconciled entry' : 'Delete entry'}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {hasMore && (
                        <div className="mt-4 flex justify-center">
                            <button
                                type="button"
                                onClick={handleLoadMore}
                                disabled={loading}
                                className="bg-white border border-[#eadfd8] text-slate-600 hover:bg-[#fff8f5] text-sm rounded-lg px-6 py-2 transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Loading…' : 'Load more'}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Delete confirmation modal */}
            <ConfirmModal
                isOpen={!!deleteTarget}
                title="Delete Transaction"
                description={deleteTarget ? (DELETE_MESSAGES[deleteTarget.reference_type] ?? DELETE_MESSAGES.OTHER) : ''}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                loading={deleteLoading}
                error={deleteError}
                onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
                onConfirm={handleDeleteConfirm}
            />

            {/* Edit modal */}
            {editTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <h2 className="text-base font-bold text-gray-800 mb-1">Edit Transaction</h2>
                        <p className="text-xs text-slate-400 mb-5">
                            {editTarget.reference_type && editTarget.transfer_group_id
                                ? 'Changes will also update the paired transfer entry.'
                                : editTarget.reference_type === 'EXPENSE'
                                    ? 'Amount and description will update the linked Expense record.'
                                    : editTarget.reference_type === 'VENDOR_PAYMENT'
                                        ? 'Amount will update the linked Vendor Ledger entry.'
                                        : editTarget.reference_type === 'OWNER_TRANS'
                                            ? `Updates owner transaction${editTarget.owner_name ? ` for ${editTarget.owner_name}` : ''}. Description, amount, date and payment reference will all sync.`
                                            : 'Updates this cashbook entry only.'}
                        </p>
                        <form onSubmit={handleEditSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                                <input
                                    type="text"
                                    value={editForm.description}
                                    onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Date</label>
                                    <input
                                        type="date"
                                        value={editForm.entry_date}
                                        onChange={e => setEditForm(f => ({ ...f, entry_date: e.target.value }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Amount (KES)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={editForm.amount}
                                        onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:border-orange-400"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Method</label>
                                    <input
                                        type="text"
                                        value={editForm.payment_method}
                                        onChange={e => setEditForm(f => ({ ...f, payment_method: e.target.value.toUpperCase() }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Reference</label>
                                    <input
                                        type="text"
                                        value={editForm.payment_reference}
                                        onChange={e => setEditForm(f => ({ ...f, payment_reference: e.target.value }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                                    />
                                </div>
                            </div>
                            {editError && <p className="text-red-500 text-xs">{editError}</p>}
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => { setEditTarget(null); setEditError(''); }}
                                    className="px-4 py-2 text-sm text-slate-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={editLoading}
                                    className="px-4 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {editLoading ? 'Saving…' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashbookTransactions;
